import { useState, useEffect } from "react";
import { useTranslations } from "next-intl";
import Link from "i18n/components/Link";
import { IAccordionItem } from "types";
import { Icon } from "@carfax/web-ui/elements/Icon";
import { keyboardAction, KeyType } from "@carfax/web-ui/core/utils/keyboardActions";
import { styles } from "./AccordionItem.styles";
import { cx } from "../../../../styled-system/css";

const useDelay = (value: boolean, delay: number): boolean => {
  const [delayedValue, setDelayedValue] = useState(value);

  useEffect(() => {
    if (value) {
      setDelayedValue(true);
      return undefined;
    }
    const timeout = setTimeout(() => {
      setDelayedValue(false);
    }, delay);

    return () => clearTimeout(timeout);
  }, [value, delay]);

  return delayedValue;
};

interface Props {
  item: IAccordionItem;
  iconFromCMS?: boolean;
}

export const AccordionItem: React.FC<Props> = ({ item }) => {
  const [isOpen, setIsOpen] = useState(false);
  const delayedIsOpen = useDelay(isOpen, 250);
  const t = useTranslations("common");

  return (
    <div className={styles.divItem(isOpen)} data-is-open={isOpen} data-testid="accordion-is-open">
      <div
        className={styles.divItemHead()}
        data-testid="accordion-expand-toggle"
        onClick={() => setIsOpen(!isOpen)}
        onKeyDown={(event) => keyboardAction(event, KeyType.ENTER, () => setIsOpen(!isOpen))}
        role="button"
        tabIndex={0}
        aria-label={
          isOpen ? t("accessibility.ariaLabelArrowUpAccordion") : t("accessibility.ariaLabelArrowDownAccordion")
        }
      >
        <div className={styles.divIconAndTitle()}>
          {item.icon ? <Icon icon={item.icon} className={cx(styles.icon(), "icon-color-override")} /> : null}

          <span className={styles.spanTitle()}>{item.title}</span>
        </div>

        <div className={styles.divHeadRight()}>
          <div className={styles.openCloseArrow()}>
            <Icon className={styles.imgOpenCloseArrow(isOpen)} icon={"/static/accordion/arrow.svg"} />
          </div>
        </div>
      </div>

      <div className={styles.divItemBody(isOpen)}>
        <div className={styles.divItemBodyInner()}>{item.body}</div>
        {item.linkUrl && delayedIsOpen && (
          <Link className={styles.link()} href={item.linkUrl}>
            &gt; {item.linkText}
          </Link>
        )}
      </div>
    </div>
  );
};
